import Link from "next/link";
import Image from "next/image";
import clsx from "clsx";

import {
  BASE_64_1X1_GREY,
  BRAND_PATHS,
  language,
  ORIGIN_IDS,
  PRODUCT_TYPE_IDS,
} from "@chef/constants";
import {
  useBillingQuery,
  PickAndMixQuery,
  usePickAndMixQuery,
  openModal,
} from "@chef/state-management";
import {
  getAttribute,
  getCurrentWeekDataAndBasketFromCalendar,
} from "@chef/state-management/helpers";
import {
  AccountReactivationButton,
  RecipeReactionButton,
  RecipeTags,
} from "@chef/smart-components";
import { isEmptyArray } from "@chef/utils/array";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import {
  isAccountPaused,
  isAllergenViolation,
  isMealboxProduct,
  isPickAndMixProduct,
  slugify,
} from "@chef/helpers";
import { MealCard, Skeleton, Tag } from "@chef/components";
import { useAppDispatch, useCalendarData } from "@chef/state-management/hooks";
import { nonNullable } from "@chef/utils/nonNullable";
import { isEqualStrings } from "@chef/utils/equal";

import type { WeekYear } from "@chef/types/common";

import UpsaleCard from "./UpsaleCard";
import { WelcomeBackSnackbar } from "./WelcomeBackSnackbar";
import { PreferenceWarningSnackbar } from "./PreferenceWarningSnackbar";
import { MyMenuCardData } from "../hooks";

import { intl } from "./WeekOverviewDefault.Intl";

type CardData =
  | Extract<MyMenuCardData, { type: "upsale" }>
  | Omit<Extract<MyMenuCardData, { type: "recipe" }>, "productId">;

interface SubTextProps {
  source?: "preference" | "self" | "default";
}

const pickRecipeImageFromPickAndMix = (
  pickAndMix: PickAndMixQuery["pickAndMix"],
  n: number,
) => {
  const images = pickAndMix
    .flatMap((p) => p.recipes)
    .map((r) => findFeaturedPortraitImage(r)?.url)
    .filter(nonNullable);

  return images[n % images.length];
};

const getGoToWeekEditorButtonText = (
  isPaused: boolean,
  hasDelivery: boolean,
) => {
  if (isPaused) {
    return intl.REACTIVATE_AND_CHANGE_DISHES;
  }

  if (hasDelivery) {
    return intl.CHANGE_DISHES;
  }

  return intl.VIEW_DISHES;
};

const SubText = ({ source = "default" }: SubTextProps) => {
  if (source === "preference") {
    return (
      <div>
        {intl.SUGGESTIONS_BASED_ON}{" "}
        <Link
          href={BRAND_PATHS.MEALBOX_PREFERENCE}
          passHref
          className="underline"
        >
          {intl.YOUR_PREFERENCES}
        </Link>
      </div>
    );
  }

  if (source === "self") {
    return <div>{intl.YOU_HAVE_CHOSEN_YOURSELF} </div>;
  }

  return null;
};

export const WeekOverviewDefault = ({ week, year }: WeekYear) => {
  const dispatch = useAppDispatch();

  const { data: billingQuery } = useBillingQuery();

  const { basket, calendar, pickAndMix } = useCalendarData({
    week,
    year,
  });
  const selectedProducts = basket.mealboxBasket;

  const currentWeekObject = getCurrentWeekDataAndBasketFromCalendar({
    calendar: calendar || [],
    week,
    year,
  });

  const productsIds = selectedProducts
    ?.filter((p) => isPickAndMixProduct(p) || isMealboxProduct(p))
    .map((p) => p.productId);

  const { data: pickAndMixQuery } = usePickAndMixQuery({
    week,
    year,
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
  });

  const products = pickAndMixQuery?.pickAndMix.filter((p) =>
    productsIds.includes(p.productId),
  );

  const productRecipes = products?.flatMap((p) => p.recipes) || [];

  const cards: CardData[] = [...Array(5)].map((_, i) => {
    const recipe = productRecipes[i];

    if (recipe) {
      return {
        key: `recipe-${recipe.recipeId}`,
        type: "recipe",
        recipe,
      };
    } else {
      return {
        key: `upsale-${i}`,
        type: "upsale",
        img: pickRecipeImageFromPickAndMix(pickAndMix || [], i),
      };
    }
  });

  const { isBeforeStartDateWeek, mealboxBasket } = currentWeekObject || {};

  const {
    basketDetails: { products: basketProducts = [], originId = "" } = {},
    shippingDetails: { hasDelivery = false } = {},
  } = mealboxBasket || {};

  const basketMealbox = basketProducts.find((p) =>
    isMealboxProduct(p.variation.product),
  );

  if (!basketMealbox) {
    return (
      <>
        <div className="col-span-full">
          <Skeleton height={12} width={80} className="mb-2" />
          <Skeleton width={64} className="mb-2" />
          <div className="flex mb-2">
            <Skeleton width={16} className="mr-4" />
            <Skeleton width={16} className="mr-4" />
          </div>
        </div>
        <section className="grid grid-cols-2 gap-4 col-span-full lg:grid-cols-5">
          {[...Array(5)].map((_, i) => {
            return (
              <div className="flex flex-col gap-2 aspect-1/2" key={i}>
                <Skeleton height="80%" />
                <Skeleton width="33%" height={6} />
                <Skeleton width="67%" height={4} />
                <Skeleton width="50%" height={4} />
              </div>
            );
          })}
        </section>
      </>
    );
  }

  const meals = getAttribute("Meals", basketMealbox.variation);
  const portions = getAttribute("Portions", basketMealbox.variation);

  if (!meals || !portions) {
    return null;
  }

  const deliveryPlanned = hasDelivery && !isBeforeStartDateWeek;

  const hasPreferenceDishes = isEqualStrings(
    originId,
    ORIGIN_IDS.RECOMMENDATIONS,
  );

  const hasSelfPickedDishes = originId?.toUpperCase() === ORIGIN_IDS.NORMAL;

  const isPausedAccount = isAccountPaused(billingQuery?.billing.status || 10);

  let pickSource: "self" | "preference" | "default" = "default";

  if (hasPreferenceDishes) {
    pickSource = "preference";
  } else if (hasSelfPickedDishes) {
    pickSource = "self";
  }

  let deliveryStatus: "freezed" | "paused" | "active" = "active";

  if (!deliveryPlanned) {
    deliveryStatus = "paused";
  }

  if (isPausedAccount) {
    deliveryStatus = "freezed";
  }

  const showPreferenceError = basketProducts.some((product) =>
    isAllergenViolation(product.allergenComplianceId),
  );

  return (
    <>
      <div className="col-span-full">
        <PreferenceWarningSnackbar show={showPreferenceError} />

        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1>
              <strong>{intl.YOUR_DISHES}</strong>
            </h1>
            <div className="mt-1 text-sm">
              <SubText source={pickSource} />
            </div>
            <div className="my-3 space-x-2">
              <Tag>
                {meals} {intl.MEALS}
              </Tag>
              <Tag>
                {portions} {intl.PORTIONS}
              </Tag>
            </div>
          </div>
          <AccountReactivationButton week={week} year={year} full={false}>
            {getGoToWeekEditorButtonText(isPausedAccount, deliveryPlanned)}
          </AccountReactivationButton>
        </div>
        <section className="relative grid grid-cols-2 gap-4 lg:grid-cols-5">
          {isEmptyArray(productRecipes) && (
            <div className="absolute top-0 left-0 right-0 z-10 flex justify-center w-full h-full pt-10 place-items-start lg:pt-0 lg:place-items-center bg-background/70">
              <WelcomeBackSnackbar isPausedAccount={isPausedAccount} />
            </div>
          )}
          {cards.map((card, index) => {
            const { key, type } = card;

            if (type === "upsale") {
              return (
                <UpsaleCard
                  key={key}
                  href={BRAND_PATHS.WEEK_EDITOR_PAGE({ week, year })}
                  imgUrl={card.img}
                  deliveryStatus={deliveryStatus}
                />
              );
            }

            const recipe = card.recipe;

            const matchingProduct = basketProducts.find((product) =>
              recipe.portions?.some((portion) =>
                isEqualStrings(portion.variationId, product.variationId),
              ),
            );

            const dishIsNonCompliant = isAllergenViolation(
              matchingProduct?.allergenComplianceId,
            );

            const url =
              findFeaturedPortraitImage(recipe)?.url || BASE_64_1X1_GREY;

            return (
              <div key={key} className="relative">
                <MealCard
                  className={clsx(
                    "bg-white rounded shadow",
                    dishIsNonCompliant && "border-2 border-error",
                    !deliveryPlanned && "opacity-50",
                    "h-full",
                  )}
                  id={matchingProduct?.variationId}
                  language={language}
                  text={recipe.recipeName}
                  tags={<RecipeTags recipe={recipe} />}
                  href={`/${intl.RECIPE}/${recipe.recipeId}/${slugify(
                    recipe.recipeName,
                  )}`}
                  Image={() => (
                    <Image
                      src={url}
                      width={2}
                      height={3}
                      priority={index < 5}
                      alt={recipe.recipeName}
                      className="object-cover w-full h-auto rounded-t aspect-2/3"
                      sizes="(max-width: 375px) 50w, (max-width: 768px) 35vw, 20vw"
                    />
                  )}
                  onClick={(e) => {
                    e?.preventDefault();

                    dispatch(
                      openModal({
                        id: "RECIPE",
                        recipeId: recipe.recipeId,
                        misc: {
                          portionSize: portions,
                        },
                        view: "deviation",
                      }),
                    );
                  }}
                  hideAddRemoveProductButton
                />
                <RecipeReactionButton
                  recipe={recipe}
                  className="absolute z-10 shadow top-4 right-4"
                />
              </div>
            );
          })}
        </section>
      </div>
    </>
  );
};
